import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { ClientUser } from '@sondermind/utilities/models-user-client';

/**
 * The possible setup steps a user can be on.
 */
export enum SetupStep {
  BASIC_INFO = 'basic_info',
  EAP = 'eap',
  INSURANCE = 'insurance',
  INSURANCE_REVIEW = 'insurance_review',
  INSURANCE_VERIFICATION_FAILED = 'insurance_verification_failed',
  COST_ESTIMATION = 'cost_estimation',
  PAYMENT = 'payment',
  EBCG_PAYMENT = 'ebcg_payment',
  COMPLETE = 'complete'
}

/**
 * Race condition flags that track user actions that may not be reflected immediately in backend state.
 */
export interface ISetupState {
  attestationsJustSucceeded: boolean;
  insuranceJustSucceeded: boolean;
  insuranceJustRejected: boolean;
  selfPayJustSelected: boolean;
  payorDowntimeJustAccepted: boolean;
  costEstimationReviewed: boolean;
  insuranceSetupHadBvError: boolean;
  eapJustDeclined: boolean;
}

/**
 * Configuration that affects step determination logic.
 */
export interface ISetupConfig {
  isProposeATime: boolean;
  isRequestToSchedule: boolean;
  useInSessionRequest: boolean;
  isSaasInvited: boolean;
  isInsuranceEmpty: boolean;
  hasSelfPayInsuranceRecord: boolean;
  shouldUseEbcgPayment: boolean;
}

/**
 * State machine service that centralizes account setup step determination logic.
 *
 * Responsibilities:
 * - Determine which setup step the user should be on
 * - Track race condition flags to handle backend sync delays
 * - Provide reactive signals for current step and step visibility
 *
 * Benefits:
 * - Single source of truth for step logic
 * - Testable in isolation
 * - Reusable across flows
 * - Clear state transitions
 */
@Injectable()
export class AccountSetupStateMachine {
  private readonly state: WritableSignal<ISetupState> = signal({
    attestationsJustSucceeded: false,
    insuranceJustSucceeded: false,
    insuranceJustRejected: false,
    selfPayJustSelected: false,
    payorDowntimeJustAccepted: false,
    costEstimationReviewed: false,
    insuranceSetupHadBvError: false,
    eapJustDeclined: false
  });

  private readonly config: WritableSignal<ISetupConfig> = signal({
    isProposeATime: false,
    isRequestToSchedule: false,
    useInSessionRequest: false,
    isSaasInvited: false,
    isInsuranceEmpty: false,
    hasSelfPayInsuranceRecord: false,
    shouldUseEbcgPayment: false
  });

  private readonly user: WritableSignal<ClientUser | null> = signal(null);

  /**
   * Current setup step based on user state, config, and race condition flags.
   */
  readonly currentStep: Signal<SetupStep> = computed(() => {
    const currentUser = this.user();
    const currentConfig = this.config();
    const currentState = this.state();

    if (!currentUser) {
      return SetupStep.BASIC_INFO;
    }

    if (this.accountComplete(currentUser)) {
      return SetupStep.COMPLETE;
    }

    if (this.missingBasicInfo(currentUser, currentState)) {
      return SetupStep.BASIC_INFO;
    }

    if (this.missingEAPInfo(currentUser, currentConfig, currentState)) {
      return SetupStep.EAP;
    }

    if (this.missingInsuranceInfo(currentUser, currentConfig, currentState)) {
      return SetupStep.INSURANCE;
    }

    if (
      !currentConfig.isProposeATime &&
      !currentConfig.isRequestToSchedule &&
      !currentConfig.useInSessionRequest &&
      !currentConfig.isSaasInvited &&
      !currentState.costEstimationReviewed
    ) {
      return SetupStep.COST_ESTIMATION;
    }

    if (this.missingFinancialInfo(currentUser, currentConfig, currentState)) {
      if (currentConfig.shouldUseEbcgPayment) {
        return SetupStep.EBCG_PAYMENT;
      }
      return SetupStep.PAYMENT;
    }

    return SetupStep.COMPLETE;
  });

  /**
   * Individual step visibility signals derived from currentStep.
   */
  readonly onBasicInfo = computed(() => this.currentStep() === SetupStep.BASIC_INFO);
  readonly onEAP = computed(() => this.currentStep() === SetupStep.EAP);
  readonly onInsurance = computed(() => this.currentStep() === SetupStep.INSURANCE);
  readonly onInsuranceReview = computed(() => this.currentStep() === SetupStep.INSURANCE_REVIEW);
  readonly onInsuranceVerificationFailed = computed(() => this.currentStep() === SetupStep.INSURANCE_VERIFICATION_FAILED);
  readonly onCostEstimation = computed(() => this.currentStep() === SetupStep.COST_ESTIMATION);
  readonly onPayment = computed(() => this.currentStep() === SetupStep.PAYMENT);
  readonly onEbcgPayment = computed(() => this.currentStep() === SetupStep.EBCG_PAYMENT);
  readonly accountCompleted = computed(() => this.currentStep() === SetupStep.COMPLETE);

  /**
   * Expose specific state flags as reactive signals for component use.
   */
  readonly insuranceSetupHadBvError = computed(() => this.state().insuranceSetupHadBvError);
  readonly selfPayJustSelected = computed(() => this.state().selfPayJustSelected);

  /**
   * Update the user data.
   */
  setUser(user: ClientUser | null): void {
    this.user.set(user);
  }

  /**
   * Update configuration.
   */
  setConfig(config: Partial<ISetupConfig>): void {
    this.config.update((current) => ({ ...current, ...config }));
  }

  /**
   * Update a single state flag.
   */
  updateState(stateUpdate: Partial<ISetupState>): void {
    this.state.update((current) => ({ ...current, ...stateUpdate }));
  }

  /**
   * Reset all race condition flags (called when moving to a new step).
   */
  resetRaceFlags(): void {
    this.state.update((current) => ({
      ...current,
      attestationsJustSucceeded: false,
      insuranceJustSucceeded: false,
      insuranceJustRejected: false,
      selfPayJustSelected: false,
      payorDowntimeJustAccepted: false,
      costEstimationReviewed: false,
      eapJustDeclined: false
    }));
  }

  /**
   * Reset specific flags when transitioning to a different step.
   */
  resetFlagsForStepTransition(targetStep: SetupStep): void {
    if (targetStep === SetupStep.BASIC_INFO || targetStep === SetupStep.EAP || targetStep === SetupStep.INSURANCE) {
      this.state.update((current) => ({
        ...current,
        insuranceJustSucceeded: false,
        selfPayJustSelected: false,
        payorDowntimeJustAccepted: false,
        costEstimationReviewed: false
      }));
    }

    // eapJustDeclined must survive the INSURANCE transition (it's what keeps the user off the
    // EAP step while they complete insurance), so only clear it when returning to BASIC_INFO.
    if (targetStep === SetupStep.BASIC_INFO) {
      this.state.update((current) => ({ ...current, eapJustDeclined: false }));
    }
  }

  /**
   * Get current state (for debugging/logging).
   */
  getState(): ISetupState {
    return this.state();
  }

  /**
   * Check if basic info step is needed.
   */
  private missingBasicInfo(user: ClientUser, state: ISetupState): boolean {
    if (state.attestationsJustSucceeded) {
      return false;
    }

    const { firstName, lastName, birthDate, phoneNumber, mailingAddress, genderId } = user?.profile || {};
    const isProfileInfoComplete =
      !!firstName &&
      !!lastName &&
      !!birthDate &&
      !!phoneNumber &&
      !!genderId &&
      !!mailingAddress?.addressLine1 &&
      !!mailingAddress?.city &&
      !!mailingAddress?.state &&
      !!mailingAddress?.postalCode;

    return user?.requiresProfileVerification
      || user?.requiresHipaaAgreement
      || user?.requiresTosAgreement
      || !isProfileInfoComplete;
  }

  /**
   * Check if EAP step is needed.
   */
  private missingEAPInfo(user: ClientUser, config: ISetupConfig, state: ISetupState): boolean {
    if (config.isSaasInvited || state.eapJustDeclined) {
      return false;
    }

    return user?.hasEapPref && user?.requiresInsuranceInfo;
  }

  /**
   * Check if insurance step is needed.
   */
  private missingInsuranceInfo(user: ClientUser, config: ISetupConfig, state: ISetupState): boolean {
    if (config.isSaasInvited ||
      state.insuranceJustSucceeded ||
      state.insuranceJustRejected ||
      state.selfPayJustSelected ||
      state.payorDowntimeJustAccepted ||
      config.hasSelfPayInsuranceRecord) {
      return false;
    }

    return user?.requiresInsuranceInfo || config.isInsuranceEmpty;
  }

  /**
   * Check if payment/financial info step is needed.
   */
  private missingFinancialInfo(user: ClientUser, config: ISetupConfig, state: ISetupState): boolean {
    if (state.selfPayJustSelected) {
      return true;
    }

    if (state.payorDowntimeJustAccepted) {
      return true;
    }

    if (state.insuranceJustSucceeded && !state.costEstimationReviewed) {
      return false;
    }

    return user?.requiresCreditCard || user?.requiresFinancialAgreement;
  }

  /**
   * Check if account setup is complete.
   */
  private accountComplete(user: ClientUser | null): boolean {
    return user != null && !user.needsSetup;
  }
}
